/**
 * The launch card's Start and Review twins, with no modal open. Modelled on
 * `HomeLabPage/focusGroupLaunch.ts`: both API edges injected, failures returned, never thrown.
 *
 * The room is linked to the card atomically, on create (`settings.sourceMessageToken`), so a
 * room can never exist with a card that still says Start. No level is sent: the server stamps
 * `DEFAULT_NEW_ROOM_LEVEL`, which is the level `conversationLaunchRate` quotes.
 */
import { DEFAULT_NEW_ROOM_LEVEL } from '@digital-twins/shared-types'
import type { CreateChatRoomDto } from '@digital-twins/shared-types'
import type { PreparedConversation, RoomSuggestion } from '@digital-twins/shared-types/assistant'

import { conversationCreditRate } from '@/components/chat/ConversationCreditQuote/ConversationCreditQuote'
import { assistantSessionsApi, chatRoomsApi } from '@/lib/api/client'
import type { TwinPickOutcome } from '@/lib/api/client'
import { blockedFeatureMessage } from '@/lib/blocked-feature-error'
import { roomBaseRate } from '@/lib/billing/room-pricing'
import { detectTrialLimitError } from '@/lib/billing/trial-limits'
import { getThreadPlaceholders } from '@/lib/constants/languages'

export interface ConversationLaunchDeps {
  pickTwins: typeof assistantSessionsApi.pickTwins
  createRoom: (dto: CreateChatRoomDto) => Promise<{ token: string }>
}

export function defaultConversationLaunchDeps(): ConversationLaunchDeps {
  return {
    pickTwins: (audienceToken, request) => assistantSessionsApi.pickTwins(audienceToken, request),
    createRoom: (dto) => chatRoomsApi.create(dto),
  }
}

export interface ConversationLaunchInput {
  prepared: PreparedConversation
  threadToken: string
  sourceMessageToken: string
  language: string | null
  /** The language of the card's own copy. Thread chrome, not the room's language. */
  copyLanguage?: string | null
}

export interface ConversationLaunchStarted {
  status: 'started'
  roomToken: string
  participantCount: number
}

export interface ConversationLaunchReview {
  status: 'review'
  personaTokens: Array<string>
  reason: string
  outcome: TwinPickOutcome
  /** Terms with no data, plus chips of dropped conditions. Empty means a full match. */
  notFound: Array<string>
}

export interface ConversationLaunchFailed {
  status: 'failed'
  reason: 'no_twins' | 'no_room_token' | 'trial_capped' | 'failed'
  /** Already user-facing: the card renders it verbatim. */
  message: string
}

export type ConversationLaunchResult = ConversationLaunchStarted | ConversationLaunchReview | ConversationLaunchFailed

export type ConversationPickResult = ConversationLaunchReview | ConversationLaunchFailed

/**
 * How long one API step of a launch may stay unanswered: above the pick's 20 s model deadline. Per
 * step and not around the whole launch, so a pick past its deadline never goes on to create a room
 * the card already reported as failed. Without it a hung request holds the guard until a reload.
 */
export const LAUNCH_STEP_DEADLINE_MS = 60_000

function withinStepDeadline<T>(step: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('Conversation launch step passed its deadline')), LAUNCH_STEP_DEADLINE_MS)
  })
  return Promise.race([step, deadline]).finally(() => clearTimeout(timer))
}

function cardCopy(key: 'startFailed' | 'noTwins', copyLanguage?: string | null): string {
  return getThreadPlaceholders(copyLanguage).conversationLaunch[key]
}

function startFailed(reason: ConversationLaunchFailed['reason'], copyLanguage?: string | null): ConversationLaunchFailed {
  return { status: 'failed', reason, message: cardCopy('startFailed', copyLanguage) }
}

function toFailure(error: unknown, copyLanguage?: string | null): ConversationLaunchFailed {
  if (detectTrialLimitError(error) === 'rooms') {
    return startFailed('trial_capped', copyLanguage)
  }
  const blocked = blockedFeatureMessage(error)
  if (blocked) {
    return { status: 'failed', reason: 'failed', message: blocked }
  }
  return startFailed('failed', copyLanguage)
}

/** The server's pick for this card, for the count the model chose. Empty is a failure. */
export async function pickConversationTwins(
  prepared: PreparedConversation,
  deps: ConversationLaunchDeps = defaultConversationLaunchDeps(),
  copyLanguage?: string | null,
): Promise<ConversationPickResult> {
  try {
    const picked = await withinStepDeadline(deps.pickTwins(prepared.twins.audienceToken, {
      populationFilter: prepared.twins.populationFilter,
      keywords: prepared.twins.keywords,
      count: prepared.participantCount,
      ...(copyLanguage ? { language: copyLanguage } : {}),
    }))
    if (picked.personaTokens.length === 0) {
      return { status: 'failed', reason: 'no_twins', message: cardCopy('noTwins', copyLanguage) }
    }
    return {
      status: 'review',
      personaTokens: picked.personaTokens,
      reason: picked.reason,
      outcome: picked.outcome,
      notFound: picked.notFound ?? [],
    }
  } catch (error) {
    return toFailure(error, copyLanguage)
  }
}

/**
 * Whether the pick is the segment the card names. The server reads the twins' own answers for the
 * keywords: `answers` with nothing left unfound, or the card's filter alone, is the segment. A
 * text mention, an unmatched keyword (even with a filter on the card) or a dropped condition is
 * something the user should see first.
 */
function isCardSegment(outcome: TwinPickOutcome, notFound: Array<string>): boolean {
  if (outcome === 'filter') {
    return true
  }
  return outcome === 'answers' && notFound.length === 0
}

/*
 * Launches still running, by the message whose card asked. The card can unmount while the pick
 * waits on the model and the launch carries on, so the guard cannot live on the card.
 */
const launchesRunning = new Map<string, Promise<ConversationLaunchResult>>()

/** The launch still running for this message, for a card mounted after it began. */
export function runningConversationLaunch(sourceMessageToken: string): Promise<ConversationLaunchResult> | undefined {
  return launchesRunning.get(sourceMessageToken)
}

/** One launch per message at a time: a second call while one runs joins it, so no second room. */
export function launchConversation(
  input: ConversationLaunchInput,
  deps: ConversationLaunchDeps = defaultConversationLaunchDeps(),
): Promise<ConversationLaunchResult> {
  const running = launchesRunning.get(input.sourceMessageToken)
  if (running) {
    return running
  }
  const launch = pickAndCreateRoom(input, deps).finally(() => {
    launchesRunning.delete(input.sourceMessageToken)
  })
  launchesRunning.set(input.sourceMessageToken, launch)
  return launch
}

/**
 * Pick, then create. Nothing is created without the user seeing it when the pick is not what the
 * card promised: keywords the twins' answers did not hold, a filter condition the
 * server could not apply, or a group that came back as one twin, which also changes the rate the
 * card showed. Fewer twins of the same kind is the same rate, and proceeds.
 */
async function pickAndCreateRoom(
  input: ConversationLaunchInput,
  deps: ConversationLaunchDeps,
): Promise<ConversationLaunchResult> {
  const { prepared, threadToken, sourceMessageToken, language, copyLanguage } = input
  const picked = await pickConversationTwins(prepared, deps, copyLanguage)
  if (picked.status === 'failed') {
    return picked
  }
  if (!isCardSegment(picked.outcome, picked.notFound) || (prepared.participantCount >= 2 && picked.personaTokens.length < 2)) {
    return picked
  }
  try {
    const room = await withinStepDeadline(deps.createRoom({
      populationId: prepared.twins.audienceToken,
      participantPersonaIds: picked.personaTokens,
      settings: {
        ...(language ? { language } : {}),
        topics: prepared.topic,
        sourceThreadToken: threadToken,
        sourceMessageToken,
      },
    }))
    const roomToken = (room?.token ?? '').trim()
    if (roomToken.length === 0) {
      return startFailed('no_room_token', copyLanguage)
    }
    return { status: 'started', roomToken, participantCount: picked.personaTokens.length }
  } catch (error) {
    return toFailure(error, copyLanguage)
  }
}

export interface ConversationLaunchAudience {
  tier?: string | null
  chatCreditsPerMessage?: number | null
  focusGroupCreditsPerMessage?: number | null
}

/**
 * Credits per message the opening question will cost: the audience's rate for the kind, at the
 * level a new room is stamped. Same calculation as the compose window's chip, without its
 * `?? 1`: no readable rate is null, and the card then offers no Start, because a charge must
 * never come at a price the card did not show.
 */
export function conversationLaunchRate(
  audience: ConversationLaunchAudience | null | undefined,
  participantCount: number,
  isEnrolled: boolean,
): number | null {
  if (!audience) {
    return null
  }
  const isChat = participantCount === 1
  const baseRate = roomBaseRate({
    kind: isChat ? 'chat' : 'focus_group',
    tier: audience.tier,
    quotedBase: isChat ? audience.chatCreditsPerMessage : audience.focusGroupCreditsPerMessage,
    enrolled: isEnrolled,
  })
  if (baseRate === null) {
    return null
  }
  return conversationCreditRate({ baseRate, level: DEFAULT_NEW_ROOM_LEVEL, isAdvancedEnabled: false })
}

/** The compose window reads a room suggestion; a prepared conversation carries the same spec. */
export function preparedConversationSuggestion(prepared: PreparedConversation): RoomSuggestion {
  return {
    kind: 'room',
    title: prepared.topic,
    why: '',
    spec: { topic: prepared.topic, openingQuestion: prepared.openingQuestion, twins: prepared.twins },
  }
}
